import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from 'styled-components';

import CarouselDates from '../components/Carousel';
import EventNotFoundState from '../components/EventNotFoundState';
import ExclusiveCreditCardBrand from '../components/ExclusiveCreditCardBrand';
import FanMember from '../components/IdentifyMember';
import EmptyState from '../components/NoTickets';
import ResaleSoldOutState, { ResaleSoldOutVariant } from '../components/ResaleSoldOutState';
import ResaleSoldOutPlaceholder from '../components/ResaleSoldOutState/Placeholder';
import SummaryMobile from '../components/SummaryMobile';
import BackIcon from '../components/SvgCustom/svg/BackIcon';
import WaitlistModal from '../components/WaitlistModal';
import ZoomImageViewer from '../components/ZoomImageViwer';
import { CardsInfo } from '../constants/cards';
import { DictonaryKeys } from '../enums/noTickets';
import { useBreadCrumbsContext } from '../hooks/contexts/breadCrumb.context';
import { useCartContext } from '../hooks/contexts/cart.context';
import { DataCarouselProps, useEventContext } from '../hooks/contexts/event.context';
import { useI18nContext } from '../hooks/contexts/i18n.context';
import { useModalContext } from '../hooks/contexts/modal.context';
import { ListView, useUiContext } from '../hooks/contexts/ui.context';
import { useUserContext } from '../hooks/contexts/user.context';
import { useResaleAvailabilityCheck } from '../hooks/useResaleAvailabilityCheck';
import { hasJoinedWaitlist } from '../services/waitlistService';
import * as Styles from '../styles/pages/tickets';

type SoldOutScenario =
  | { kind: 'none' }
  | { kind: 'pending' }
  | { kind: 'resale'; variant: ResaleSoldOutVariant };

// Empty-screen reasons with no official sale: sold out, disabled by the organizer, or
// sold out on a football event. In these cases the page never shows the old generic empty
// state: it always uses one of the ResaleSoldOutState states (listing, waitlist or
// explore). The generic empty state is kept only for errors loading the ticket list.
const OFFICIAL_SALE_OVER_REASONS: DictonaryKeys[] = [
  DictonaryKeys.soldOut, DictonaryKeys.salesIsNotEnable, DictonaryKeys.football,
];

const TicketsPage = () => {
  const {
    allTicketsUnavailable,
    loading,
    startImage,
    isFootball,
    isBotafogo,
    hasTermModal,
    singleDayDate,
    selectedIdDateCarousel,
    dataCarousel,
    noTicketContentKey,
  } = useEventContext();
  const { translate } = useI18nContext();
  const { isPaymentPage } = useBreadCrumbsContext();
  const { isMobile, handleRenderListView, listView, handleChangeListView } = useUiContext();
  const { summaryData, combosInCart } = useCartContext();
  const {
    event,
    applyPassKey,
    applyCoupon,
    openModalBotafogo,
    showNoTicketsContent,
    openTermModal
  } = useEventContext();
  const theme = useTheme();
  const [queryParam] = useSearchParams();
  const passkey = queryParam.get('passkey');
  const coupon = queryParam.get('coupon');
  const { modalIsOpen, setContentModal, toggleModal, defineTitleModal } = useModalContext();
  const { isTicketPage } = useBreadCrumbsContext();
  const [selectedSession, setSelectedSession] = useState<DataCarouselProps>();

  const navigate = useNavigate();
  const noTicketsToDisplay = showNoTicketsContent
    && noTicketContentKey !== DictonaryKeys.eventNotFound
    && noTicketContentKey !== DictonaryKeys.ticketListError;
  const { hasResale, loading : resaleAvailabilityLoading } = useResaleAvailabilityCheck(
    noTicketsToDisplay ? event?.id : undefined
  );

  const alreadyJoined = event?.id ? hasJoinedWaitlist(event.id) : true;

  const { renewToken, handleAuthenticatedUser, authenticatedUser, user } = useUserContext();

  const openWaitlistModal = useCallback(() => {
    defineTitleModal(translate(['RESALE_LISTING', 'WAITLIST', 'TITLE']));
    setContentModal(
      <WaitlistModal
        eventId={event!.id}
        userId={user?.id}
      />
    );
    toggleModal({ open : true });
  }, [defineTitleModal, translate, setContentModal, event?.id, user?.id, toggleModal]);

  const handleBuyResaleClick = useCallback(
    () => navigate(`/resale/event/${event?.id}`),
    [navigate, event?.id]
  );

  // The explore variant has no handler: its CTA is a link to the site (Environment.SITE_URL).
  const soldOutCtaHandlers: Record<Exclude<ResaleSoldOutVariant, 'explore'>, () => void> = {
    listing : handleBuyResaleClick,
    waitlist : openWaitlistModal,
  };

  const soldOutScenario = useMemo(
    (): SoldOutScenario => {
      if (!noTicketsToDisplay) return { kind : 'none' };
      // The state can't be chosen until the resale check finishes; showing the generic
      // empty state in the meantime made the old page "flash" on screen. During that window
      // (capped by a timeout in the hook) the area shows the final state's skeleton.
      if (resaleAvailabilityLoading) return { kind : 'pending' };
      if (!OFFICIAL_SALE_OVER_REASONS.includes(noTicketContentKey)) return { kind : 'none' };
      // Resale open: "Venda oficial esgotada" state with the CTA to the listing.
      if (hasResale) return { kind : 'resale', variant : 'listing' };
      const waitlistActive = Boolean(event?.attributes.secWaitlist?.value);
      if (waitlistActive && !alreadyJoined) return { kind : 'resale', variant : 'waitlist' };
      // No official sale, no resale and no waitlist available: explore other events.
      return { kind : 'resale', variant : 'explore' };
    },
    [
      noTicketsToDisplay, resaleAvailabilityLoading, hasResale, alreadyJoined,
      noTicketContentKey, event?.attributes.secWaitlist?.value,
    ]
  );

  const renderEmptyState = () => {
    if (noTicketContentKey === DictonaryKeys.eventNotFound) return <EventNotFoundState />;
    if (soldOutScenario.kind === 'pending') {
      return (
        <Fragment>
          {isFootball && <FanMember size="30%" />}
          <ResaleSoldOutPlaceholder />
        </Fragment>
      );
    }
    if (soldOutScenario.kind === 'resale') {
      const { variant } = soldOutScenario;
      return (
        <Fragment>
          {isFootball && <FanMember size="30%" />}
          {variant === 'explore' ? (
            <ResaleSoldOutState variant={variant} />
          ) : (
            <ResaleSoldOutState variant={variant} onCtaClick={soldOutCtaHandlers[variant]} />
          )}
        </Fragment>
      );
    }
    return <EmptyState />;
  };

  useEffect(() => {
    renewToken();
  }, []);

  const showSummaryInMobile = isMobile && (summaryData.length > 0 || combosInCart.length > 0);
  const fullWidthContainer = isPaymentPage || allTicketsUnavailable || isMobile;
  const hasAUniqueSession = isFootball || singleDayDate;

  useEffect(() => {
    setSelectedSession(dataCarousel.find(({ id }) => id === selectedIdDateCarousel));
  }, [selectedIdDateCarousel]);

  useEffect(() => {
    if (passkey && event?.id && event?.producerId) {
      applyPassKey({ passkey, modalIsOpen, setContentModal, toggleModal });
    }
  }, [passkey, event?.id, event?.producerId]);

  useEffect(() => {
    if (coupon && event?.id && event?.producerId) {
      applyCoupon({ coupon, modalIsOpen, setContentModal, toggleModal });
    }
  }, [coupon, event?.id, event?.producerId]);

  useEffect(() => {
    if (isTicketPage && authenticatedUser) {
      handleAuthenticatedUser(false);
    }
  }, [isTicketPage]);

  useEffect(() => {
    if (isBotafogo) {
      openModalBotafogo({ setContentModal, toggleModal });
    }
  }, [isBotafogo]);

  useEffect(() => {
    if(hasTermModal) {
      openTermModal({ setContentModal, toggleModal });
    }
  }, [hasTermModal]);

  const exclusiveCreditCardBrand = event?.attributes.exclusiveCreditCardBrand?.value;

  return (
    <Styles.Container
      id="tickets-page"
      $fullWidth={fullWidthContainer}
      $summaryMobileIsOpen={showSummaryInMobile}
    >
      {exclusiveCreditCardBrand && <ExclusiveCreditCardBrand {...CardsInfo[exclusiveCreditCardBrand]} />}
      {
        (!isPaymentPage && !showNoTicketsContent ) ? (
          <Fragment>
            {isMobile ? (
              <Fragment>
                <CarouselDates />
                <Styles.WrapperContent $showStartImage={Boolean(startImage)}>
                  {startImage && (
                    <Styles.WrapperStartImage id="start-image-wrapper">
                      <Styles.ContainerImage id="start-image-container">
                        <ZoomImageViewer pathImage={startImage.large} />
                      </Styles.ContainerImage>
                    </Styles.WrapperStartImage>
                  )}
                  <div>
                    {listView === ListView.TICKETS && !hasAUniqueSession && (
                      <Styles.BackToSessionListContainer>
                        <Styles.RedirectText
                          onClick={() => handleChangeListView(ListView.SESSIONS)}
                        >
                          <img src={BackIcon({ colorTheme : theme.colors.accent.primary })} alt="seta voltando" />
                          {translate(['TICKET_TEMPLATE', 'VIEW_SESSIONS'])}
                        </Styles.RedirectText>
                        <Styles.RightContent>
                          <h3>{selectedSession?.title}</h3>
                          <span>{selectedSession?.subtitle}</span>
                        </Styles.RightContent>
                      </Styles.BackToSessionListContainer>
                    )}

                    {handleRenderListView()}
                  </div>
                </Styles.WrapperContent>
              </Fragment>
            ) : (
              <Fragment>
                <CarouselDates />
                <Styles.WrapperContent $showStartImage={Boolean(startImage)}>
                  {startImage && (
                    <Styles.WrapperStartImage id="start-image-wrapper">
                      <Styles.ContainerImage id="start-image-container">
                        <ZoomImageViewer pathImage={startImage.large} />
                      </Styles.ContainerImage>
                    </Styles.WrapperStartImage>
                  )}
                  {handleRenderListView()}
                </Styles.WrapperContent>
              </Fragment>
            )}
          </Fragment>
        ) : !loading && renderEmptyState()
      }
      {showSummaryInMobile && <SummaryMobile />}
    </Styles.Container>
  );
};

export default TicketsPage;
